import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { Id } from '@contracts/common';
import type {
  DelegationBlockDTO, DelegationDecideBody, DelegationListDTO, DelegationNudgeDTO, DelegationProgressDTO, DelegationRespondBody,
  DelegationTrackBody,
} from '@contracts/delegation';
import { api, ApiClientError, offlineError } from '../../api/client';
import { notify } from '@/lib/toast';
import { NUDGES_KEY } from '../assistant/proactive/api';
import { BOARD_KEY } from '../workplace/useBoard';
import { CHANGED } from './format';

// AI S9 L3 (task list §1.2): the "Giao việc" block's reads and writes. One mutation hook maps every refusal the
// same way wherever a button sits (the block, the bell, "Nhờ tôi").

const ITEMS = '/api/workplace/items';
export const delegationKey = (itemId: Id) => ['delegation', itemId] as const;
export const DELEGATIONS_KEY = ['delegations'] as const;
const NOTIFICATIONS_KEY = ['notifications'] as const;

/** The card's delegation as its viewer sees it; `null` for anyone else (a 404 is hidden = missing, also `null`). */
export const fetchDelegation = async (itemId: Id): Promise<DelegationBlockDTO | null> => {
  try {
    return await api<DelegationBlockDTO | null>(`${ITEMS}/${itemId}/delegation`);
  } catch (e) {
    if (e instanceof ApiClientError && e.status === 404) return null;
    throw e;
  }
};

export function useDelegation(itemId: Id): UseQueryResult<DelegationBlockDTO | null, ApiClientError> {
  return useQuery<DelegationBlockDTO | null, ApiClientError>({
    queryKey: delegationKey(itemId), queryFn: () => fetchDelegation(itemId), retry: false,
  });
}

/** `GET /api/delegations?side=` (§8.3): "Tôi đã giao" (`mine`) and "Nhờ tôi" (`for_me`); every write invalidates it. */
export const delegationListKey = (side: DelegationListDTO['side']) => [...DELEGATIONS_KEY, side] as const;

export function useDelegationList(side: DelegationListDTO['side'], enabled = true): UseQueryResult<DelegationListDTO, ApiClientError> {
  return useQuery<DelegationListDTO, ApiClientError>({
    queryKey: delegationListKey(side), enabled, retry: false,
    queryFn: async () => {
      try {
        return await api<DelegationListDTO>(`/api/delegations?side=${side}`);
      } catch (e) {
        throw e instanceof ApiClientError ? e : offlineError();
      }
    },
  });
}

/** `POST …/progress` (§8.2): one request id per open popover, so a double click counts once. */
export const askProgress = (itemId: Id, requestId: Id): Promise<DelegationProgressDTO> =>
  api<DelegationProgressDTO>(`${ITEMS}/${itemId}/delegation/progress`, { method: 'POST', body: {}, requestId });

/** `POST …/nudge` (D82): stages the delegator's pending `work.nudge` card; nothing is sent until it is confirmed. */
export const stageNudge = (itemId: Id, requestId: Id): Promise<DelegationNudgeDTO> =>
  api<DelegationNudgeDTO>(`${ITEMS}/${itemId}/delegation/nudge`, { method: 'POST', body: {}, requestId });

export type DelegationWrite =
  | { op: 'respond'; body: DelegationRespondBody }
  | { op: 'decide'; body: DelegationDecideBody }
  | { op: 'track'; body: DelegationTrackBody };

function send(itemId: Id, w: DelegationWrite, requestId: Id): Promise<DelegationBlockDTO> {
  const base = `${ITEMS}/${itemId}/delegation`;
  if (w.op === 'track') return api<DelegationBlockDTO>(base, { method: 'PATCH', body: w.body, requestId });
  return api<DelegationBlockDTO>(`${base}/${w.op}`, { method: 'POST', body: w.body, requestId });
}

/** A refusal the caller says in place (a field error inside its popover); every other one is told here. */
export const isFieldRefusal = (e: unknown): boolean =>
  e instanceof ApiClientError && e.code === 'DEADLINE_NOT_LATER';

/**
 * Every delegation button's write (§10): both 409s say the spec's sentence and read the block again; 429 says the
 * server's words; `DEADLINE_NOT_LATER` is left to the caller's field; anything else is the server's message. The
 * block the server answers is the new state (a tracking flip bumps `version`, an outcome or cadence save does not:
 * never patched locally).
 */
export function useDelegationWrite(itemId: Id) {
  const queryClient = useQueryClient();
  return useMutation<DelegationBlockDTO, ApiClientError, DelegationWrite>({
    mutationFn: async (w) => {
      try {
        return await send(itemId, w, crypto.randomUUID());
      } catch (e) {
        throw e instanceof ApiClientError ? e : offlineError();
      }
    },
    onSuccess: (block, w) => {
      queryClient.setQueryData(delegationKey(itemId), block);
      void queryClient.invalidateQueries({ queryKey: DELEGATIONS_KEY });
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      // An accepted request moves the card's deadline (service.ts:255): the card and the board read it again.
      if (w.op === 'decide' && w.body.decision === 'accept') {
        void queryClient.invalidateQueries({ queryKey: ['task', itemId] });
        void queryClient.invalidateQueries({ queryKey: BOARD_KEY });
      }
      // A question is written as a comment on the card (service.ts:218): the open card's timeline reads it (lens 1 M3).
      if (w.op === 'respond' && w.body.action === 'question') {
        void queryClient.invalidateQueries({ queryKey: ['task', itemId] });
      }
    },
    onError: (e) => {
      if (isFieldRefusal(e)) return;
      if (e.status === 409) {
        notify.warning(CHANGED);
        void queryClient.invalidateQueries({ queryKey: delegationKey(itemId) });
        void queryClient.invalidateQueries({ queryKey: DELEGATIONS_KEY });
        return;
      }
      if (e.status === 429) { notify.warning(e.message); return; }
      notify.error(e.message);
    },
  });
}

/** After a progress ask or a nudge: the pair count on the block and the tray read again (§6.5). */
export function useContactRefresh(itemId: Id) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: delegationKey(itemId) });
    void queryClient.invalidateQueries({ queryKey: NUDGES_KEY });
  };
}
